import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import type {
  AgentAuxiliaryCandidatePage,
  AgentAuxiliaryCandidateQuery,
  AgentAvailabilityMutation,
  AgentDetailQuery,
  AgentEscritorioQuery,
  AgentEscritorioPage,
  AgentCatalogPage,
  AgentCatalogQuery,
  AgentCatalogItem,
  AgentInternalLine,
  AgentLinesQuery,
  AgentListQuery,
  AgentMutation,
  AgentMutationResult,
  AgentPage,
  AgentResource,
  AgentResourceQuery,
  AgentValidation,
  AgentValidationQuery,
  FlowValidationProblem,
  NegotiationAgent,
} from '../../types/negotiationAgents';
import type {
  AgentRunningMigrationApplyInput,
  AgentRunningMigrationPreview,
  AgentRunningMigrationResult,
} from '../../types/agentRunningMigration';
import { agentCatalogQueryKey, agentQueryKey } from '../../utils/negotiationAgents/queryKey';
import { toAgentProblem } from '../../utils/negotiationAgents/apiResponse';
import { negotiationAgentsService } from './services';

export const fetchNegotiationAgents = createAsyncThunk<
  { key: string; page: AgentPage<NegotiationAgent> },
  AgentListQuery,
  { state: Pick<RootState, 'negotiationAgents'>; rejectValue: FlowValidationProblem }
>(
  'negotiationAgents/list',
  async (query, { rejectWithValue }) => {
    try {
      return { key: agentQueryKey(query), page: await negotiationAgentsService.list(query) };
    } catch (error) {
      return rejectWithValue(toAgentProblem(error));
    }
  },
  {
    condition: (query, { getState }) =>
      getState().negotiationAgents.lists[agentQueryKey(query)]?.status !== 'loading',
  },
);
export const fetchNegotiationAgent = createAsyncThunk<
  NegotiationAgent,
  AgentDetailQuery,
  { state: Pick<RootState, 'negotiationAgents'>; rejectValue: FlowValidationProblem }
>(
  'negotiationAgents/detail',
  async (query, { rejectWithValue }) => {
    try {
      return await negotiationAgentsService.detail(typeof query === 'string' ? query : query.id);
    } catch (error) {
      return rejectWithValue(toAgentProblem(error));
    }
  },
  {
    condition: (query, { getState }) =>
      typeof query !== 'string' ||
      getState().negotiationAgents.detailStatus[query]?.status !== 'loading',
  },
);
export const fetchAgentResources = createAsyncThunk<
  { key: string; page: AgentPage<AgentResource> },
  AgentResourceQuery,
  { state: Pick<RootState, 'negotiationAgents'>; rejectValue: FlowValidationProblem }
>(
  'negotiationAgents/resources',
  async (query, { rejectWithValue }) => {
    try {
      return { key: agentQueryKey(query), page: await negotiationAgentsService.resources(query) };
    } catch (error) {
      return rejectWithValue(toAgentProblem(error));
    }
  },
  {
    condition: (query, { getState }) =>
      getState().negotiationAgents.resources[agentQueryKey(query)]?.status !== 'loading',
  },
);
export const validateNegotiationAgent = createAsyncThunk<
  AgentValidation,
  string | AgentValidationQuery,
  { rejectValue: FlowValidationProblem }
>('negotiationAgents/validate', async (id, { rejectWithValue }) => {
  try {
    return await negotiationAgentsService.validate(typeof id === 'string' ? { agentId: id } : id);
  } catch (error) {
    return rejectWithValue(toAgentProblem(error));
  }
});
export const mutateNegotiationAgent = createAsyncThunk<
  AgentMutationResult,
  AgentMutation & { key: string },
  { rejectValue: FlowValidationProblem }
>('negotiationAgents/mutate', async ({ key, ...input }, { rejectWithValue }) => {
  try {
    return { key, result: await negotiationAgentsService.mutate(input) };
  } catch (error) {
    return rejectWithValue(toAgentProblem(error));
  }
});

export const fetchAgentCatalog = createAsyncThunk<
  { key: string; page: AgentCatalogPage<AgentCatalogItem> },
  AgentCatalogQuery,
  { state: Pick<RootState, 'negotiationAgents'>; rejectValue: FlowValidationProblem }
>(
  'negotiationAgents/catalog',
  async (query, { rejectWithValue }) => {
    try {
      return {
        key: agentCatalogQueryKey(query),
        page: await negotiationAgentsService.catalogs(query),
      };
    } catch (error) {
      return rejectWithValue(toAgentProblem(error));
    }
  },
  {
    condition: (query, { getState }) =>
      getState().negotiationAgents.catalogs[agentCatalogQueryKey(query)]?.status !== 'loading',
  },
);

export const fetchAgentOffices = createAsyncThunk<
  { key: string; page: AgentEscritorioPage },
  AgentEscritorioQuery,
  { state: Pick<RootState, 'negotiationAgents'>; rejectValue: FlowValidationProblem }
>(
  'negotiationAgents/offices',
  async (query, { rejectWithValue }) => {
    try {
      return { key: agentQueryKey(query), page: await negotiationAgentsService.offices(query) };
    } catch (error) {
      return rejectWithValue(toAgentProblem(error));
    }
  },
  {
    condition: (query, { getState }) =>
      getState().negotiationAgents.offices[agentQueryKey(query)]?.status !== 'loading',
  },
);

/** Aplica na hora, sem publicar: o core grava a disponibilidade fora do rascunho versionado. */
export const updateAgentAvailability = createAsyncThunk<
  NegotiationAgent,
  AgentAvailabilityMutation,
  { rejectValue: FlowValidationProblem }
>('negotiationAgents/updateAvailability', async (input, { rejectWithValue }) => {
  try {
    return await negotiationAgentsService.updateAvailability(input);
  } catch (error) {
    return rejectWithValue(toAgentProblem(error));
  }
});

export const previewAgentRunningMigration = createAsyncThunk<
  AgentRunningMigrationPreview,
  string,
  { state: Pick<RootState, 'negotiationAgents'>; rejectValue: FlowValidationProblem }
>(
  'negotiationAgents/runningMigrationPreview',
  async (agentId, { rejectWithValue }) => {
    try {
      return await negotiationAgentsService.previewRunningMigration(agentId);
    } catch (error) {
      return rejectWithValue(toAgentProblem(error));
    }
  },
  {
    condition: (agentId, { getState }) =>
      getState().negotiationAgents.runningMigrationPreviews[agentId]?.status !== 'loading',
  },
);

/** 409 = o plano mudou desde a prévia (o `planHash` não confere); quem chama refaz a prévia. */
export const applyAgentRunningMigration = createAsyncThunk<
  AgentRunningMigrationResult,
  AgentRunningMigrationApplyInput,
  { state: Pick<RootState, 'negotiationAgents'>; rejectValue: FlowValidationProblem }
>(
  'negotiationAgents/runningMigrationApply',
  async (input, { rejectWithValue }) => {
    try {
      return await negotiationAgentsService.applyRunningMigration(input);
    } catch (error) {
      return rejectWithValue(toAgentProblem(error));
    }
  },
  {
    condition: ({ agentId }, { getState }) =>
      getState().negotiationAgents.runningMigrationResults[agentId]?.status !== 'loading',
  },
);

export const fetchAgentLines = createAsyncThunk<
  { key: string; page: AgentPage<AgentInternalLine> },
  AgentLinesQuery,
  { state: Pick<RootState, 'negotiationAgents'>; rejectValue: FlowValidationProblem }
>(
  'negotiationAgents/lines',
  async (query, { rejectWithValue }) => {
    try {
      return { key: agentQueryKey(query), page: await negotiationAgentsService.lines(query) };
    } catch (error) {
      return rejectWithValue(toAgentProblem(error));
    }
  },
  {
    condition: (query, { getState }) =>
      getState().negotiationAgents.lines[agentQueryKey(query)]?.status !== 'loading',
  },
);

export const fetchAgentAuxiliaryCandidates = createAsyncThunk<
  { key: string; page: AgentAuxiliaryCandidatePage },
  AgentAuxiliaryCandidateQuery,
  { state: Pick<RootState, 'negotiationAgents'>; rejectValue: FlowValidationProblem }
>(
  'negotiationAgents/auxiliaryCandidates',
  async (query, { rejectWithValue }) => {
    try {
      return {
        key: agentCatalogQueryKey(query),
        page: await negotiationAgentsService.auxiliaryCandidates(query),
      };
    } catch (error) {
      return rejectWithValue(toAgentProblem(error));
    }
  },
  {
    condition: (query, { getState }) =>
      getState().negotiationAgents.auxiliaryCandidates[agentCatalogQueryKey(query)]?.status !==
      'loading',
  },
);
